import {useContext, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {doc, getDoc, getFirestore, setDoc} from 'firebase/firestore';
import {getAuth} from 'firebase/auth';
import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {
  TEACHNOW_PRESENCE_HEARTBEAT_MS,
  isTeachNowEnabledForAnyRole,
} from '@cip/core/teachnow';
import {TimeContext} from 'contexts/TimeContext';
import {useCourseId} from 'hooks/router/useUrlParams';
import {getIdeRunActivity, startIdeRunActivity} from './ideRunActivity';

/** Edits or runs within this long keep the heartbeat writing. */
const ACTIVE_WITHIN_MS = 2 * 60 * 1000;

/** A tab must stay hidden this long before presence says so. */
const HIDDEN_DEBOUNCE_MS = 5 * 1000;

/** How long past another tab's hidden debounce a visible tab re-asserts. */
const VISIBLE_REASSERT_SLACK_MS = 3000;

const IDE_PATH_MARKER = 'ide/a';

function getAssnIdFromPath(pathname: string): string | null {
  if (!pathname.includes(IDE_PATH_MARKER)) {
    return null;
  }
  return pathname.split(IDE_PATH_MARKER)[1] ?? null;
}

/**
 * Keeps the TeachNow fields of the student's presence doc
 * (`TeachNowPresenceSignals`) current in courses where any role has the
 * `teachnow` feature (`isTeachNowEnabledForAnyRole`): a heartbeat every `TEACHNOW_PRESENCE_HEARTBEAT_MS` while they are
 * editing or running code in the IDE, `hiddenSinceMs` while the tab is
 * hidden, and `activeAssnId: null` once they leave the IDE or close the tab.
 * Other courses are untouched.
 *
 * @param args.isStudent - Whether the user is a student in this course.
 * @param args.isMobile - Whether they are on a small screen.
 * @return Whether this course tracks TeachNow presence; when it does, this
 *   hook owns the IDE heartbeat.
 * @throws Never; write failures are logged to the console.
 */
export function useTeachNowPresence({
  isStudent,
  isMobile,
}: {
  isStudent: boolean;
  isMobile: boolean;
}): boolean {
  const db = getFirestore();
  const courseId = useCourseId();
  const location = useLocation();
  const {getServerTimeMs, hasEditedCodeSinceUpdateRef} =
    useContext(TimeContext);
  // The course the TeachNow flag was read for, so a stale answer for the
  // previous course never applies to this one.
  const [teachNowCourseId, setTeachNowCourseId] = useState<string | null>(
    null,
  );
  const hasTeachNow = Boolean(courseId) && teachNowCourseId === courseId;
  const assnId = getAssnIdFromPath(location.pathname);
  const hiddenSinceMsRef = useRef<number | null>(null);
  // Client clock: the server offset may not be known yet at entry, so it is
  // applied when the time is used.
  const enteredAtClientMsRef = useRef<number | null>(null);
  // Intervals and listeners outlive renders; they read these through the ref
  // so they see the current server clock, not the one from when they started.
  const latestRef = useRef({isStudent, isMobile, getServerTimeMs});
  latestRef.current = {isStudent, isMobile, getServerTimeMs};

  useEffect(() => {
    if (!courseId) return;
    let isCancelled = false;
    getDoc(getCourseDocumentRef({courseId, firestore: db}))
      .then(snapshot => {
        if (!isCancelled) {
          setTeachNowCourseId(
            isTeachNowEnabledForAnyRole(snapshot.data()) ? courseId : null,
          );
        }
      })
      .catch(error => {
        console.debug('[engagement] course features read failed', error);
      });
    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- db is a singleton
  }, [courseId]);

  const userId = getAuth()?.currentUser?.uid;
  const isTracking = hasTeachNow && Boolean(userId) && Boolean(courseId);

  const getNowMs = (): number => {
    const serverMs = latestRef.current.getServerTimeMs?.();
    return typeof serverMs === 'number' && Number.isFinite(serverMs)
      ? serverMs
      : Date.now();
  };

  const writePresence = (
    fields: Record<string, unknown>,
    target: {courseId?: string; userId?: string} = {courseId, userId},
  ) => {
    if (!target.userId || !target.courseId) return;
    try {
      setDoc(doc(db, 'presence', target.courseId, 'users', target.userId), fields, {
        merge: true,
      }).catch(error => {
        console.debug('[engagement] TeachNow presence write failed', error);
      });
    } catch (error) {
      console.debug('[engagement] TeachNow presence write failed', error);
    }
  };

  const enteredAtMs = (): number | null => {
    const enteredClientMs = enteredAtClientMsRef.current;
    return enteredClientMs === null
      ? null
      : enteredClientMs + (getNowMs() - Date.now());
  };

  const lastActivityMs = (): number | null => {
    const editedAt: unknown = hasEditedCodeSinceUpdateRef?.current;
    const enteredMs = enteredAtMs();
    // The edit time is shared across the app, so an edit made before this
    // assignment was opened does not count as activity on it.
    const editMs =
      editedAt instanceof Date &&
      Number.isFinite(editedAt.getTime()) &&
      (enteredMs === null || editedAt.getTime() >= enteredMs)
        ? editedAt.getTime()
        : null;
    const {lastRunMs} = getIdeRunActivity();
    const serverDeltaMs = getNowMs() - Date.now();
    const runMs = lastRunMs === null ? null : lastRunMs + serverDeltaMs;
    if (editMs === null) return runMs;
    if (runMs === null) return editMs;
    return Math.max(editMs, runMs);
  };

  // Entering an assignment resets its counts; leaving the IDE takes the
  // student out of matching right away.
  const isEntryPendingRef = useRef(false);
  // Opening an assignment is not an edit or a run, so lastActivityMs is
  // cleared rather than set; the next edit or run fills it in.
  const writeEntry = (enteredAssnId: string) => {
    isEntryPendingRef.current = false;
    const activity = getIdeRunActivity();
    writePresence({
      activeAssnId: enteredAssnId,
      runsOnAssn: activity.runsOnAssn,
      failedRunsOnAssn: activity.failedRunsOnAssn,
      lastRunFailed: activity.lastRunFailed,
      lastActivityMs: null,
      hiddenSinceMs: null,
    });
  };

  // The leave write goes to the course the student entered, which may not be
  // the one they are in now.
  const enteredRef = useRef<{
    assnId: string;
    courseId: string;
    userId: string;
  } | null>(null);
  useEffect(() => {
    const entered = enteredRef.current;
    const isSameEntry =
      entered !== null &&
      entered.assnId === assnId &&
      entered.courseId === courseId &&
      entered.userId === userId;
    if (entered && !isSameEntry) {
      enteredRef.current = null;
      isEntryPendingRef.current = false;
      startIdeRunActivity(null);
      enteredAtClientMsRef.current = null;
      writePresence({activeAssnId: null}, entered);
    }
    if (!isTracking || !assnId || !courseId || !userId || isSameEntry) {
      return;
    }
    enteredRef.current = {assnId, courseId, userId};
    startIdeRunActivity(assnId);
    enteredAtClientMsRef.current = Date.now();
    hiddenSinceMsRef.current = null;
    // A tab opened in the background writes nothing yet: it must not
    // overwrite the signals of the tab the student is working in. It writes
    // its entry once it is viewed, or reports hidden after the usual debounce.
    if (document.visibilityState === 'hidden') {
      isEntryPendingRef.current = true;
      return;
    }
    writeEntry(assnId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- write helpers read refs
  }, [assnId, isTracking, courseId, userId]);

  // Unmounting (a course switch remounts the page) is a leave too.
  useEffect(
    () => () => {
      const entered = enteredRef.current;
      if (!entered) return;
      enteredRef.current = null;
      startIdeRunActivity(null);
      writePresence({activeAssnId: null}, entered);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount only; writes go to the captured entry
    [],
  );

  useEffect(() => {
    if (!isTracking || !assnId) return;
    // Presence is one doc shared by all of the student's tabs, so only a
    // visible tab may report activity; a hidden one only reports that it is.
    const beat = () => {
      if (document.visibilityState === 'hidden') return;
      if (isEntryPendingRef.current) {
        writeEntry(assnId);
      }
      const activityMs = lastActivityMs();
      const nowMs = getNowMs();
      if (activityMs === null || nowMs - activityMs > ACTIVE_WITHIN_MS) {
        // Still the tab in view: undo anything another tab wrote (hidden,
        // or a null on close) without claiming activity.
        hiddenSinceMsRef.current = null;
        writePresence({activeAssnId: assnId, hiddenSinceMs: null});
        return;
      }
      const activity = getIdeRunActivity();
      // Never ahead of the server clock, whatever the client clock says.
      const engagedMs = Math.min(
        nowMs,
        Math.max(activityMs, enteredAtMs() ?? activityMs),
      );
      hiddenSinceMsRef.current = null;
      writePresence({
        activeAssnId: assnId,
        lastEngagedTime: new Date(engagedMs),
        lastActivityMs: activityMs,
        runsOnAssn: activity.runsOnAssn,
        failedRunsOnAssn: activity.failedRunsOnAssn,
        lastRunFailed: activity.lastRunFailed,
        hiddenSinceMs: null,
        isStudent: latestRef.current.isStudent,
        isMobile: latestRef.current.isMobile,
      });
    };
    const interval = setInterval(beat, TEACHNOW_PRESENCE_HEARTBEAT_MS);

    let hiddenTimer: ReturnType<typeof setTimeout> | undefined;
    let visibleTimer: ReturnType<typeof setTimeout> | undefined;
    // Another of the student's tabs may write "hidden" when its own debounce
    // ends; if this tab is still in view by then, say so again.
    const scheduleVisibleReassert = () => {
      visibleTimer = setTimeout(() => {
        if (document.visibilityState !== 'visible') return;
        writePresence({activeAssnId: assnId, hiddenSinceMs: null});
      }, HIDDEN_DEBOUNCE_MS + VISIBLE_REASSERT_SLACK_MS);
    };
    const onVisibilityChange = () => {
      clearTimeout(hiddenTimer);
      clearTimeout(visibleTimer);
      if (document.visibilityState === 'hidden') {
        const hiddenAtMs = getNowMs();
        hiddenTimer = setTimeout(() => {
          if (document.visibilityState !== 'hidden') return;
          hiddenSinceMsRef.current = hiddenAtMs;
          writePresence({hiddenSinceMs: hiddenAtMs});
        }, HIDDEN_DEBOUNCE_MS);
        return;
      }
      if (isEntryPendingRef.current) {
        hiddenSinceMsRef.current = null;
        writeEntry(assnId);
      } else if (hiddenSinceMsRef.current !== null) {
        hiddenSinceMsRef.current = null;
        writePresence({activeAssnId: assnId, hiddenSinceMs: null});
      }
      scheduleVisibleReassert();
    };
    if (document.visibilityState === 'hidden') {
      if (hiddenSinceMsRef.current === null) {
        onVisibilityChange();
      }
    } else {
      scheduleVisibleReassert();
    }
    // Best effort: writes started during unload often never leave, and the
    // engaged window drops the student soon after the heartbeat stops.
    const onPageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      writePresence({activeAssnId: null});
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(interval);
      clearTimeout(hiddenTimer);
      clearTimeout(visibleTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- write helpers read refs
  }, [assnId, isTracking, courseId, userId]);
  return hasTeachNow;
}
